import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import useConfirmationModal from '../../hooks/useConfirmationModal';
import { ICoupon } from '../../interfaces/coupon';
import { callApi } from '../../api/callApi';
import { API_ENDPOINT, API_URL_2 } from '../../api/constant';
import ConfirmationModal from '../../components/UI/Modal/ConfirmationModal';
import Modal from '../../components/UI/Modal/Modal';
import moment from '../../utility/moment-timezone';
import Table from '../../components/UI/CommonTable/CommonTable';

interface IUsageLog {
    id: string;
    couponId: string;
    userId: string;
    orderId: string;
    discountApplied: number;
    orderTotal: number;
    couponCode: string;
    createdAt: string;
    updatedAt: string;
    order: {
        personalInfo: {
            email: string;
            phone: string;
            lastName: string;
            firstName: string;
        };
        id: string;
        orderId: string;
    };
}

interface IUsageStats {
    totalUsage: number;
    uniqueUsers: number;
    totalDiscountGiven: number;
    usageToday: number;
    recentUsages: IUsageLog[];
}

const Coupons: React.FC = () => {
    const [coupons, setCoupons] = useState<ICoupon[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const { showModal, modalProps, confirmAction } = useConfirmationModal();
    const navigate = useNavigate();

    const [usageModal, setUsageModal] = useState<boolean>(false);
    const [usageStats, setUsageStats] = useState<IUsageStats | null>(null);
    const [usageLoading, setUsageLoading] = useState<boolean>(false);
    const [selectedCouponCode, setSelectedCouponCode] = useState<string>('');

    useEffect(() => {
        getCoupons();
    }, []);

    const getCoupons = async () => {
        try {
            setLoading(true);
            const response = await callApi(API_URL_2 + API_ENDPOINT.order + 'coupons', { method: 'GET' });
            console.log('Coupons fetched:', response);
            setCoupons(response);
        } catch (error: any) {
            console.error('Failed to fetch coupons:', error.message);
        } finally {
            setLoading(false);
        }
    };

    const deleteCoupon = async (id: string) => {
        try {
            await callApi(API_URL_2 + API_ENDPOINT.order + `/coupons/${id}`, { method: 'DELETE' });
            setCoupons((prevCoupons) => prevCoupons.filter((coupon) => coupon.id !== id));
        } catch (error: any) {
            console.error('Failed to delete coupon:', error.message);
        }
    };

    const handleDeleteClick = (id: string) => {
        confirmAction({
            title: 'Confirm Delete',
            message: 'Are you sure you want to delete this coupon?',
            onConfirm: () => deleteCoupon(id),
        });
    };

    const handleViewUsageLogs = async (coupon: ICoupon) => {
        setSelectedCouponCode(coupon.code);
        setUsageModal(true);
        setUsageLoading(true);
        setUsageStats(null);
        try {
            const response: IUsageStats = await callApi(
                API_URL_2 + API_ENDPOINT.order + `coupons/${coupon.id}/usage-stats`,
                { method: 'GET' }
            );
            setUsageStats(response);
        } catch (error: any) {
            console.error('Failed to fetch usage stats:', error.message);
        } finally {
            setUsageLoading(false);
        }
    };

    const columns = [
        { header: "Code", accessor: "code" },
        { header: "Description", accessor: "description" },
        {
            header: "Value Type",
            accessor: "type",
            render: (type: string) => (type === 'percentage' ? 'Percentage' : 'Flat'),
        },
        { header: "Discount", accessor: "discountValue" },
        {
            header: "Start Date",
            accessor: "startDate",
            render: (startDate: string) => moment(startDate).format("DD MMM, YYYY"),
        },
        {
            header: "Expiration Date",
            accessor: "expirationDate",
            render: (expirationDate: string) => moment(expirationDate).format("DD MMM, YYYY"),
        },

        { header: "Minimum Value", accessor: "minimumValue" },
        {
            header: "Usage Limits",
            accessor: "maxTotalUsage",
            render: (_: any, coupon: ICoupon) => (
                <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
                    <div><strong>Total:</strong> {coupon.maxTotalUsage !== null && coupon.maxTotalUsage !== undefined ? coupon.maxTotalUsage : 'Unlimited'}</div>
                    <div><strong>Per User:</strong> {coupon.maxUsagePerUser !== null && coupon.maxUsagePerUser !== undefined ? coupon.maxUsagePerUser : 'Unlimited'}</div>
                    <div><strong>Per Day:</strong> {coupon.maxUsagePerDay !== null && coupon.maxUsagePerDay !== undefined ? coupon.maxUsagePerDay : 'Unlimited'}</div>
                    <div><strong>Per User/Day:</strong> {coupon.maxUsagePerUserPerDay !== null && coupon.maxUsagePerUserPerDay !== undefined ? coupon.maxUsagePerUserPerDay : 'Unlimited'}</div>
                </div>
            ),
        },
    ];

    const actions = [
        {
            icon: "bi bi-journal-text",
            color: "info",
            onClick: (coupon: ICoupon) => handleViewUsageLogs(coupon),
        },
        {
            icon: "bi bi-pen",
            color: "success",
            onClick: (coupon: ICoupon) => navigate(`/edit-coupon/${coupon.id}`),
        },
        {
            icon: "bi bi-trash3",
            color: "danger",
            onClick: (coupon: ICoupon) => handleDeleteClick(coupon.id),
        },
    ];

    const usageLogColumns = [
        {
            header: "Order ID",
            accessor: "order",
            render: (_: any, log: IUsageLog) => log.order?.orderId || '-',
        },
        {
            header: "Customer",
            accessor: "userId",
            render: (_: any, log: IUsageLog) => {
                const info = log.order?.personalInfo;
                return info ? `${info.firstName} ${info.lastName}` : '-';
            },
        },
        {
            header: "Email",
            accessor: "couponCode",
            render: (_: any, log: IUsageLog) => log.order?.personalInfo?.email || '-',
        },
        {
            header: "Discount Applied",
            accessor: "discountApplied",
            render: (val: number) => val?.toFixed(3) || '-',
        },
        {
            header: "Order Total",
            accessor: "orderTotal",
            render: (val: number) => val?.toFixed(3) || '-',
        },
        {
            header: "Date",
            accessor: "createdAt",
            render: (createdAt: string) => moment(createdAt).format("DD MMM, YYYY hh:mm A"),
        },
    ];

    return (
        <Layout>
            <div className='d-flex justify-content-between align-items-start'>
                <h1 className="app-page-title">Coupons</h1>
                <Link to={"/add-coupon"} className='btn app-btn-primary d-flex align-items-center gap-1'>
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M6 0C6.13261 0 6.25979 0.0526785 6.35355 0.146447C6.44732 0.240215 6.5 0.367392 6.5 0.5V5.5H11.5C11.6326 5.5 11.7598 5.55268 11.8536 5.64645C11.9473 5.74021 12 5.86739 12 6C12 6.13261 11.9473 6.25979 11.8536 6.35355C11.7598 6.44732 11.6326 6.5 11.5 6.5H6.5V11.5C6.5 11.6326 6.44732 11.7598 6.35355 11.8536C6.25979 11.9473 6.13261 12 6 12C5.86739 12 5.74021 11.9473 5.64645 11.8536C5.55268 11.7598 5.5 11.6326 5.5 11.5V6.5H0.5C0.367392 6.5 0.240215 6.44732 0.146447 6.35355C0.0526785 6.25979 0 6.13261 0 6C0 5.86739 0.0526785 5.74021 0.146447 5.64645C0.240215 5.55268 0.367392 5.5 0.5 5.5H5.5V0.5C5.5 0.367392 5.55268 0.240215 5.64645 0.146447C5.74021 0.0526785 5.86739 0 6 0Z" fill="white" />
                    </svg> Create a Coupon
                </Link>
            </div>
            <div className="app-card app-card-orders-table shadow-sm mb-4 p-4">
                <div className="app-card-body">
                    <Table
                        columns={columns}
                        data={coupons}
                        loading={loading}
                        actions={actions}
                        emptyMessage="No Orders available."
                    />
                </div>
            </div>

            <ConfirmationModal {...modalProps} show={showModal} />

            {/* Usage Logs Modal */}
            <Modal
                show={usageModal}
                onClose={() => setUsageModal(false)}
                title={`Usage Logs — ${selectedCouponCode}`}
                size="xl"
            >
                {usageLoading ? (
                    <div className="text-center py-4">Loading...</div>
                ) : usageStats ? (
                    <>
                        {/* Summary Stats */}
                        <div className="row mb-4">
                            <div className="col-md-3 col-6 mb-3">
                                <div className="app-card app-card-settings shadow-sm p-3 text-center">
                                    <div className="fw-bold" style={{ fontSize: '24px' }}>{usageStats.totalUsage}</div>
                                    <div className="text-muted" style={{ fontSize: '13px' }}>Total Usage</div>
                                </div>
                            </div>
                            <div className="col-md-3 col-6 mb-3">
                                <div className="app-card app-card-settings shadow-sm p-3 text-center">
                                    <div className="fw-bold" style={{ fontSize: '24px' }}>{usageStats.uniqueUsers}</div>
                                    <div className="text-muted" style={{ fontSize: '13px' }}>Unique Users</div>
                                </div>
                            </div>
                            <div className="col-md-3 col-6 mb-3">
                                <div className="app-card app-card-settings shadow-sm p-3 text-center">
                                    <div className="fw-bold" style={{ fontSize: '24px' }}>{usageStats.totalDiscountGiven?.toFixed(3)}</div>
                                    <div className="text-muted" style={{ fontSize: '13px' }}>Total Discount Given</div>
                                </div>
                            </div>
                            <div className="col-md-3 col-6 mb-3">
                                <div className="app-card app-card-settings shadow-sm p-3 text-center">
                                    <div className="fw-bold" style={{ fontSize: '24px' }}>{usageStats.usageToday}</div>
                                    <div className="text-muted" style={{ fontSize: '13px' }}>Usage Today</div>
                                </div>
                            </div>
                        </div>

                        {/* Recent Usages Table */}
                        <h6 className="fw-bold mb-3">Recent Usages</h6>
                        <Table
                            columns={usageLogColumns}
                            data={usageStats.recentUsages || []}
                            loading={false}
                            emptyMessage="No usage logs available."
                        />
                    </>
                ) : (
                    <div className="text-center py-4 text-muted">Failed to load usage stats.</div>
                )}
            </Modal>
        </Layout>
    );
};

export default Coupons;
